import React, { useMemo, useState } from 'react'
import GlassPanel from '../UI/GlassPanel'

const formatDate = (value) => {
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Who you saw, when, and where.
 *
 * Ranked by times seen, filterable by name, expandable to the individual
 * sightings — the answer to "have I seen them before, and where?".
 */
const ArtistHistorySection = ({ artistHistory = [], favoriteArtists = [] }) => {
  const [filter, setFilter] = useState('')
  const [expanded, setExpanded] = useState(null)
  const [favoritesOnly, setFavoritesOnly] = useState(false)

  const visible = useMemo(() => {
    const source = favoritesOnly ? favoriteArtists : artistHistory
    const needle = filter.trim().toLowerCase()
    if (!needle) return source
    return source.filter(artist => artist.name.toLowerCase().includes(needle))
  }, [artistHistory, favoriteArtists, favoritesOnly, filter])

  if (artistHistory.length === 0) {
    return (
      <GlassPanel className="p-6 mb-8">
        <h2 className="text-xl font-bold text-white mb-2">🎤 Who you've seen</h2>
        <p className="text-gray-400 text-sm">
          Mark artists as seen on your past events to build this up.
        </p>
      </GlassPanel>
    )
  }

  const repeats = artistHistory.filter(artist => artist.timesSeen > 1)

  return (
    <GlassPanel className="p-6 mb-8">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-xl font-bold text-white">🎤 Who you've seen</h2>
        <p className="text-sm text-gray-400">
          {artistHistory.length} artists · {repeats.length} seen more than once
        </p>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter artists…"
          className="flex-1 min-w-[12rem] px-3 py-2 bg-white/10 border border-white/25 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <button
          type="button"
          onClick={() => setFavoritesOnly(v => !v)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            favoritesOnly ? 'bg-yellow-500/25 text-yellow-100' : 'bg-white/10 text-gray-300 hover:bg-white/20'
          }`}
        >
          ★ Favourites only
        </button>
      </div>

      <div className="max-h-[26rem] overflow-y-auto pr-1 space-y-1">
        {visible.map(artist => {
          const isOpen = expanded === artist.name

          return (
            <div key={artist.name} className="bg-white/5 border border-white/10 rounded-xl">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : artist.name)}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/5 rounded-xl transition-colors"
              >
                {artist.favorite && <span className="text-yellow-400 flex-shrink-0">★</span>}
                <span className="text-white font-medium truncate flex-1">{artist.name}</span>
                <span className="text-sm text-gray-400 flex-shrink-0">
                  {artist.timesSeen}×
                </span>
                <span className={`text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`}>›</span>
              </button>

              {isOpen && (
                <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-white/10">
                  {artist.sightings.map((sighting, index) => (
                    <div key={`${sighting.eventId}-${index}`} className="text-sm">
                      <span className="text-gray-200">{sighting.eventName}</span>
                      <span className="text-gray-500">
                        {sighting.venueName ? ` · ${sighting.venueName}` : ''}
                        {sighting.place ? ` · ${sighting.place}` : ''}
                      </span>
                      <span className="text-gray-500"> · {formatDate(sighting.date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}

        {visible.length === 0 && (
          <p className="text-center text-gray-400 py-6">
            No artists match {filter ? `"${filter}"` : 'that filter'}
          </p>
        )}
      </div>
    </GlassPanel>
  )
}

export default ArtistHistorySection
